import "./Notes.css";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaPlus,
  FaSearch,
  FaTrash,
  FaRegFileAlt,
  FaUndo,
  FaRedo,
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaListUl,
  FaListOl,
  FaTable,
  FaTimes,
  FaColumns,
  FaImage,
  FaFilePdf,
} from "react-icons/fa";

import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";

import { createWorker } from "tesseract.js";

import { db } from "../../firebase/firebase";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  Timestamp,
} from "firebase/firestore";

import {
  useClientsStatusMap,
  resolveProjectNumber,
  formatProjectSeq,
} from "../../hooks/useClientsStatusMap";

import { useToast } from "./Invoicefiles/Usetoast";
import ToastContainer from "./Invoicefiles/Toastcontainer";
import ConfirmDialog from "./Invoicefiles/Confirmdialog";
import NotesProjectPicker from "./Notesprojectpicker";
import { generateNotePdf } from "./Notespdf";

// ===========================================================================
// NOTES — lightweight document workspace.
//
// Fully independent of the Clients / Estimation / Invoice / Payments /
// Materials pipeline. It has its own Firestore collection (`notes`) and
// never writes to `clients`, `estimations`, `invoices`, `budgetPlans`, or
// `paymentAllocations`.
//
// A "project note" stores only REFERENCES to an existing project — the
// same identifiers every other page already keys off:
//   clientDocId   -> the `clients` document id (the app-wide project key)
//   clientId      -> the admin-entered "CL-xxx" id
//   projectNumber -> from resolveProjectNumber() in useClientsStatusMap
// The client's live name/status is always re-read from the realtime
// clients map at render time, so a note can never show a stale copy of
// client data. No second client/project model is introduced.
//
// NOTIFICATIONS: every user-facing message on this page goes through the
// app's own useToast + ToastContainer, and every destructive or lossy
// confirmation goes through ConfirmDialog. There is deliberately no
// window.confirm / window.alert anywhere in this file — those render the
// browser's native chrome and bypass the custom toast UI entirely.
//
// DELETION POLICY (deliberate, non-cascading): deleting a note only ever
// deletes that one `notes` document. Deleting a CLIENT does not delete
// its notes — Clients.js's confirmDeleteClient batch is intentionally
// left untouched. An orphaned project note keeps its content and simply
// renders a "Project unavailable" marker rather than silently destroying
// written records or rewriting its own project reference.
//
// PDF EXPORT (read-only): "Generate PDF" hands the note's TipTap JSON to
// Notespdf.js, which renders it using the same company header/branding
// as the Invoice PDFs. It never saves, edits, or deletes anything.
// ===========================================================================

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph" }] };

const emptyDraft = () => ({
  title: "",
  noteType: "general",
  clientDocId: "",
});

// Flattens TipTap JSON to plain text — stored alongside the structured
// content purely for the list preview (and to make future note search
// cheap without parsing the document tree).
const extractPlainText = (node) => {
  if (!node) return "";
  if (node.type === "text") return node.text || "";
  if (!Array.isArray(node.content)) return "";
  return node.content.map(extractPlainText).join(" ");
};

// ===========================================================================
// IMAGE -> TEXT (OCR)
//
// Runs entirely client-side via tesseract.js. The selected image is never
// uploaded anywhere and never written to Firestore — it exists only long
// enough to be read by the OCR worker, after which its object URL is
// revoked. Only the extracted TEXT ever enters the note, and it does so
// through the same editor content path as typed text, so the existing
// save/autosave mechanism persists it with no extra field and no second
// saving mechanism.
//
// Nothing below touches the note document shape, the project reference
// fields, the table extension, or the delete flow.
// ===========================================================================

const OCR_ACCEPTED_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/bmp",
];
const OCR_MAX_FILE_BYTES = 12 * 1024 * 1024; // 12 MB
const OCR_MAX_DIMENSION = 2000; // px — longest edge fed to the OCR worker

const formatFileSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Downscales oversized images onto a canvas before OCR so a large phone
// photo can't lock up the tab. The user's original file is never modified —
// this returns either the untouched File (when small enough) or a new Blob.
const prepareImageForOcr = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const longest = Math.max(img.width, img.height);

      if (!longest) {
        reject(new Error("unreadable"));
        return;
      }

      if (longest <= OCR_MAX_DIMENSION) {
        resolve(file);
        return;
      }

      const scale = OCR_MAX_DIMENSION / longest;
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);

      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("unreadable"))),
        "image/png",
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable"));
    };

    img.src = url;
  });

// Turns raw OCR output into TipTap paragraph nodes. Blank lines separate
// paragraphs; single line breaks inside a paragraph become hard breaks, so
// address/contact blocks keep their shape. Returns [] when nothing readable
// came back — that empty result is what gates the "no text found" message,
// so an empty block is never inserted into the editor.
const ocrTextToParagraphNodes = (raw) => {
  const cleaned = (raw || "")
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+$/gm, "")
    .trim();

  if (!cleaned) return [];

  return cleaned
    .split(/\n{2,}/)
    .map((block) => {
      const lines = block
        .split("\n")
        .map((line) => line.trim())
        .filter(Boolean);

      const content = [];
      lines.forEach((line, idx) => {
        if (idx > 0) content.push({ type: "hardBreak" });
        content.push({ type: "text", text: line });
      });

      return { type: "paragraph", content };
    })
    .filter((node) => node.content.length > 0);
};

function Notes() {
  const { toasts, toast } = useToast();
  const { clientsById, clientsLoading } = useClientsStatusMap();

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [search, setSearch] = useState("");

  // activeId === null while a brand-new, not-yet-saved note is open.
  // isComposing distinguishes "nothing open" from "new note open".
  const [activeId, setActiveId] = useState(null);
  const [isComposing, setIsComposing] = useState(false);
  const [draft, setDraft] = useState(emptyDraft());
  const [formError, setFormError] = useState("");

  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Which document is currently being exported to PDF (null = none).
  // Purely local UI state — no note data, no Firestore write.
  const [pdfGeneratingKey, setPdfGeneratingKey] = useState(null);

  // Replaces the previous window.confirm() unsaved-changes prompts. Holds
  // the navigation the user asked for while the ConfirmDialog is open, so
  // it can be replayed on "Discard changes" or dropped on "Keep editing".
  // Shape: { kind: "open" | "new" | "close", note?: <note> }
  const [pendingNavigation, setPendingNavigation] = useState(null);

  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState("3");
  const [tableCols, setTableCols] = useState("3");

  // ---------------- Image -> Text (OCR) state ----------------
  const [showOcrModal, setShowOcrModal] = useState(false);
  const [ocrFile, setOcrFile] = useState(null);
  const [ocrPreviewUrl, setOcrPreviewUrl] = useState("");
  const [ocrRunning, setOcrRunning] = useState(false);
  const [ocrProgress, setOcrProgress] = useState(0);
  const [ocrError, setOcrError] = useState("");

  const ocrInputRef = useRef(null);
  const ocrPreviewUrlRef = useRef("");

  // True once the user has actually placed a caret in this note. Decides
  // whether OCR text lands at the cursor or is appended at the end — a
  // freshly opened note has its selection at position 0, and inserting
  // there would push extracted text above content already written.
  const cursorPlacedRef = useRef(false);

  // Set while content is being loaded into the editor programmatically,
  // so the onUpdate handler doesn't mark a freshly-opened note as dirty.
  const loadingContentRef = useRef(false);
  const autosaveTimerRef = useRef(null);

  // ---------------- Editor ----------------
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: EMPTY_DOC,
    onUpdate: () => {
      if (loadingContentRef.current) return;
      setDirty(true);
    },
    // Only used to decide where OCR text is inserted — it never marks the
    // note dirty and never affects saving.
    onSelectionUpdate: () => {
      if (loadingContentRef.current) return;
      cursorPlacedRef.current = true;
    },
  });

  // ---------------- Realtime notes list ----------------
  // Same onSnapshot pattern Clients.js and DashHome.js already use, so
  // the list stays live without a manual refetch.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "notes"),
      (snapshot) => {
        const list = [];
        snapshot.forEach((docSnap) => list.push({ id: docSnap.id, ...docSnap.data() }));

        list.sort((a, b) => {
          const aTime = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
          const bTime = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
          return bTime - aTime;
        });

        setNotes(list);
        setNotesLoading(false);
      },
      (err) => {
        console.error("Failed to subscribe to notes:", err);
        setNotesLoading(false);
      },
    );

    return () => unsubscribe();
  }, []);

  // ---------------- Helpers ----------------
  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      const dateObj = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (Number.isNaN(dateObj.getTime())) return "-";
      return dateObj.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "-";
    }
  };

  // "Today" / "Yesterday" / a date — for the list's Last edited column.
  const formatRelativeDate = (timestamp) => {
    if (!timestamp?.toDate) return "-";
    const d = timestamp.toDate();
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const diffDays = Math.floor(
      (startOfToday - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000,
    );
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return formatDate(timestamp);
  };

  // Every project that can be attached to a note — one entry per
  // `clients` document, since that IS a project in this app. Rebuilt from
  // the realtime clients map, so anything added, renamed, or removed in
  // Clients.js flows straight through to the picker.
  const projectOptions = useMemo(() => {
    return Object.values(clientsById)
      .map((c) => ({
        clientDocId: c.id,
        clientId: c.clientId || "",
        name: c.name || "",
        projectType: c.projectType || "",
        status: c.status || "In Talk",
        projectNumber: resolveProjectNumber(clientsById, c.id),
      }))
      .sort((a, b) => {
        const byName = (a.name || "").localeCompare(b.name || "");
        return byName !== 0 ? byName : a.projectNumber - b.projectNumber;
      });
  }, [clientsById]);

  // Live project details for a saved note. Reads the realtime clients map
  // rather than the copy stored on the note, so renamed clients update
  // everywhere. Returns null when the project no longer exists.
  const liveProjectFor = useCallback(
    (note) => {
      if (note?.noteType !== "project" || !note?.clientDocId) return null;
      const client = clientsById[note.clientDocId];
      if (!client) return null;
      return {
        name: client.name || "",
        clientId: client.clientId || "",
        projectType: client.projectType || "",
        projectNumber: resolveProjectNumber(clientsById, note.clientDocId),
      };
    },
    [clientsById],
  );

  const projectLabelFor = useCallback(
    (note) => {
      if (note.noteType !== "project") return "General";
      const live = liveProjectFor(note);
      if (!live) return "Project unavailable";
      return `${live.name} · P-${formatProjectSeq(live.projectNumber)}`;
    },
    [liveProjectFor],
  );

  const filteredNotes = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return notes;
    return notes.filter((n) => {
      const title = (n.title || "").toLowerCase();
      const preview = (n.plainText || "").toLowerCase();
      const project = projectLabelFor(n).toLowerCase();
      return title.includes(term) || preview.includes(term) || project.includes(term);
    });
  }, [search, notes, projectLabelFor]);

  // ---------------- Opening / creating ----------------
  const loadContentIntoEditor = (content) => {
    if (!editor) return;
    loadingContentRef.current = true;
    // A newly loaded document has no user-placed caret yet, so OCR text
    // appends to the end until the user clicks/types somewhere.
    cursorPlacedRef.current = false;
    editor.commands.setContent(content || EMPTY_DOC, false);
    // Cleared after the transaction settles so onUpdate ignores this write.
    requestAnimationFrame(() => {
      loadingContentRef.current = false;
    });
  };

  // The three functions below are the "commit" halves of navigation —
  // they assume the unsaved-changes question has already been answered.
  const doOpenNote = useCallback(
    (note) => {
      setActiveId(note.id);
      setIsComposing(true);
      setDraft({
        title: note.title || "",
        noteType: note.noteType || "general",
        // Restores the previously selected project automatically — the
        // picker resolves this clientDocId back to a full project card.
        clientDocId: note.clientDocId || "",
      });
      setFormError("");
      setDirty(false);
      setLastSavedAt(note.updatedAt || null);
      loadContentIntoEditor(note.content);

      if (note.noteType === "project" && note.clientDocId && !clientsById[note.clientDocId]) {
        toast.warning("The linked project is no longer available.");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clientsById, editor],
  );

  const doStartNewNote = useCallback(() => {
    setActiveId(null);
    setIsComposing(true);
    setDraft(emptyDraft());
    setFormError("");
    setDirty(false);
    setLastSavedAt(null);
    loadContentIntoEditor(EMPTY_DOC);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  const doCloseEditor = useCallback(() => {
    setActiveId(null);
    setIsComposing(false);
    setDraft(emptyDraft());
    setFormError("");
    setDirty(false);
  }, []);

  // ...and these are the "request" halves. When there are unsaved edits
  // they park the intent in pendingNavigation and let ConfirmDialog ask;
  // otherwise they run straight through.
  const openNote = (note) => {
    if (dirty) {
      setPendingNavigation({ kind: "open", note });
      return;
    }
    doOpenNote(note);
  };

  const startNewNote = () => {
    if (dirty) {
      setPendingNavigation({ kind: "new" });
      return;
    }
    doStartNewNote();
  };

  const closeEditor = () => {
    if (dirty) {
      setPendingNavigation({ kind: "close" });
      return;
    }
    doCloseEditor();
  };

  const discardAndContinue = () => {
    const nav = pendingNavigation;
    setPendingNavigation(null);
    if (!nav) return;

    if (nav.kind === "open" && nav.note) doOpenNote(nav.note);
    else if (nav.kind === "new") doStartNewNote();
    else doCloseEditor();
  };

  const handleDraftChange = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
    if (formError) setFormError("");
  };

  // ---------------- Saving ----------------
  // One code path for both create and update. addDoc only ever runs when
  // activeId is null (a genuinely new document), so editing an existing
  // note can never produce a duplicate.
  const saveNote = useCallback(
    async ({ silent = false } = {}) => {
      if (!editor) return;

      const title = draft.title.trim();

      if (!title) {
        setFormError("Please enter a title before saving.");
        if (!silent) toast.error("Please enter a title before saving.");
        return;
      }

      if (draft.noteType === "project" && !draft.clientDocId) {
        setFormError("Please select a project before saving this note.");
        if (!silent) toast.error("Please select a project before saving this note.");
        return;
      }

      setSaving(true);

      try {
        const content = editor.getJSON();
        const now = Timestamp.now();

        const base = {
          title,
          noteType: draft.noteType,
          content,
          plainText: extractPlainText(content).slice(0, 400),
          updatedAt: now,
        };

        // Project fields are written as a block, and explicitly nulled for
        // a general note, so switching a note from Project to General can
        // never leave a stale project reference behind.
        const projectFields =
          draft.noteType === "project"
            ? (() => {
                const client = clientsById[draft.clientDocId] || {};
                return {
                  clientDocId: draft.clientDocId,
                  clientId: client.clientId || "",
                  clientName: client.name || "",
                  projectNumber: resolveProjectNumber(clientsById, draft.clientDocId),
                  projectType: client.projectType || "",
                };
              })()
            : {
                clientDocId: null,
                clientId: null,
                clientName: null,
                projectNumber: null,
                projectType: null,
              };

        const payload = { ...base, ...projectFields };
        const isCreate = !activeId;

        if (activeId) {
          // createdAt is deliberately absent — it must never change.
          await updateDoc(doc(db, "notes", activeId), payload);
        } else {
          const ref = await addDoc(collection(db, "notes"), {
            ...payload,
            createdAt: now,
          });
          setActiveId(ref.id);
        }

        setDirty(false);
        setLastSavedAt(now);
        setFormError("");

        if (!silent) {
          toast.success(isCreate ? "Note created successfully." : "Note saved successfully.");
        }
      } catch (err) {
        // Raw Firebase errors stay in the console for debugging; the user
        // sees a plain-language message.
        console.error("Failed to save note:", err);
        toast.error("Unable to save the note. Please try again.");
      } finally {
        setSaving(false);
      }
    },
    [editor, draft, activeId, clientsById, toast],
  );

  // Autosave — only for a note that already exists in Firestore, 3s after
  // typing stops. A brand-new note is never autosaved, so an abandoned
  // blank draft never becomes a stray document. The explicit Save button
  // stays the primary path and is unaffected.
  useEffect(() => {
    if (!dirty || !activeId || saving) return;

    autosaveTimerRef.current = setTimeout(() => {
      saveNote({ silent: true });
    }, 3000);

    return () => clearTimeout(autosaveTimerRef.current);
  }, [dirty, activeId, saving, saveNote]);

  // Ctrl/Cmd+S saves without leaving the keyboard.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "s" && isComposing) {
        e.preventDefault();
        saveNote();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isComposing, saveNote]);

  // ---------------- Delete ----------------
  // Deletes exactly one `notes` document. No client, estimation, invoice,
  // payment, budget plan, or material record is touched.
  const confirmDeleteNote = async () => {
    if (!pendingDelete) return;

    setDeleting(true);
    try {
      await deleteDoc(doc(db, "notes", pendingDelete.id));

      if (activeId === pendingDelete.id) {
        doCloseEditor();
      }

      toast.success("Note deleted successfully.");
    } catch (err) {
      console.error("Failed to delete note:", err);
      toast.error("Unable to delete the note. Please try again.");
    } finally {
      setDeleting(false);
      setPendingDelete(null);
    }
  };

  // ---------------- Generate PDF (read-only export) ----------------
  // Hands the note's TipTap JSON to Notespdf.js, which renders it with the
  // same company header/branding used by the Invoice PDFs. Nothing here
  // saves, edits, or deletes a note, and no Firestore write happens.
  const buildNotePdfPayload = ({
    title,
    noteType,
    clientDocId,
    content,
    createdAt,
    updatedAt,
  }) => {
    const client = noteType === "project" && clientDocId ? clientsById[clientDocId] : null;

    return {
      title,
      noteType,
      content,
      projectLabel:
        noteType === "project"
          ? client
            ? `${client.name || "Unnamed client"} · P-${formatProjectSeq(
                resolveProjectNumber(clientsById, clientDocId),
              )}`
            : "Project unavailable"
          : "",
      projectClientId: client?.clientId || "",
      projectType: client?.projectType || "",
      createdLabel: formatDate(createdAt),
      updatedLabel: formatDate(updatedAt),
    };
  };

  const runNotePdf = async (key, payload) => {
    if (pdfGeneratingKey) return;
    setPdfGeneratingKey(key);
    try {
      await generateNotePdf(payload);
      toast.success("PDF generated successfully.");
    } catch (err) {
      console.error("Failed to generate note PDF:", err);
      toast.error("Unable to generate the PDF. Please try again.");
    } finally {
      setPdfGeneratingKey(null);
    }
  };

  // From the sidebar list — exports the note exactly as stored in Firestore.
  const generatePdfForSavedNote = (note) => {
    runNotePdf(
      note.id,
      buildNotePdfPayload({
        title: note.title || "Untitled document",
        noteType: note.noteType || "general",
        clientDocId: note.clientDocId,
        content: note.content,
        createdAt: note.createdAt,
        updatedAt: note.updatedAt,
      }),
    );
  };

  // From the open editor — exports what is currently on screen.
  const generatePdfForOpenNote = () => {
    if (!editor) return;
    const saved = notes.find((n) => n.id === activeId);
    runNotePdf(
      activeId,
      buildNotePdfPayload({
        title: draft.title.trim() || "Untitled document",
        noteType: draft.noteType,
        clientDocId: draft.clientDocId,
        content: editor.getJSON(),
        createdAt: saved?.createdAt || null,
        updatedAt: saved?.updatedAt || lastSavedAt,
      }),
    );
  };

  // ---------------- Table insertion ----------------
  const openTableModal = () => {
    setTableRows("3");
    setTableCols("3");
    setShowTableModal(true);
  };

  const insertTable = () => {
    const rows = Math.max(1, Math.min(20, Number(tableRows) || 0));
    const cols = Math.max(1, Math.min(10, Number(tableCols) || 0));
    editor?.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
    setShowTableModal(false);
  };

  const inTable = editor?.isActive("table");

  // ---------------- Image -> Text (OCR) ----------------
  const setOcrPreview = (url) => {
    if (ocrPreviewUrlRef.current) URL.revokeObjectURL(ocrPreviewUrlRef.current);
    ocrPreviewUrlRef.current = url;
    setOcrPreviewUrl(url);
  };

  const resetOcrState = () => {
    setShowOcrModal(false);
    setOcrFile(null);
    setOcrError("");
    setOcrProgress(0);
    setOcrPreview("");
    if (ocrInputRef.current) ocrInputRef.current.value = "";
  };

  const openOcrModal = () => {
    setOcrError("");
    setOcrProgress(0);
    setShowOcrModal(true);
  };

  const closeOcrModal = () => {
    if (ocrRunning) return; // never yank the modal away mid-extraction
    resetOcrState();
  };

  const clearOcrFile = () => {
    if (ocrRunning) return;
    setOcrFile(null);
    setOcrError("");
    setOcrPreview("");
    if (ocrInputRef.current) ocrInputRef.current.value = "";
  };

  const handleOcrFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // lets the same file be re-picked after a removal
    if (!file) return;

    if (!OCR_ACCEPTED_TYPES.includes(file.type)) {
      setOcrError(
        "That file type isn't supported. Please choose a JPG, PNG, WEBP, GIF or BMP image.",
      );
      return;
    }

    if (file.size > OCR_MAX_FILE_BYTES) {
      setOcrError("That image is too large. Please choose an image under 12 MB.");
      return;
    }

    setOcrError("");
    setOcrFile(file);
    setOcrPreview(URL.createObjectURL(file));
  };

  // Runs Tesseract in a worker, then inserts the result as ordinary editor
  // content. A fresh worker per run keeps memory flat; the language data is
  // cached by the browser after the first extraction.
  //
  // Insertion rules (requirement 5 + 14):
  //   - empty editor, or the user has placed a caret -> insert at cursor
  //   - otherwise                                    -> append at the end
  // Nothing is ever replaced, so repeated extractions stack up instead of
  // overwriting each other.
  const runOcr = async () => {
    if (!editor || !ocrFile || ocrRunning) return;

    setOcrRunning(true);
    setOcrError("");
    setOcrProgress(0);

    let worker = null;

    try {
      const prepared = await prepareImageForOcr(ocrFile);

      worker = await createWorker("eng", 1, {
        logger: (m) => {
          if (m.status === "recognizing text") {
            setOcrProgress(Math.round((m.progress || 0) * 100));
          }
        },
      });

      const { data } = await worker.recognize(prepared);
      const nodes = ocrTextToParagraphNodes(data?.text || "");

      if (nodes.length === 0) {
        const message =
          "No readable text was found in this image. Try a clearer or higher-resolution image.";
        setOcrError(message);
        toast.warning(message);
        return; // nothing is inserted — the note is left exactly as it was
      }

      if (editor.isEmpty || cursorPlacedRef.current) {
        editor.chain().focus().insertContent(nodes).run();
      } else {
        editor.chain().focus("end").insertContent(nodes).run();
      }

      cursorPlacedRef.current = true;
      setDirty(true); // existing Save / autosave takes it from here

      toast.success("Text extracted successfully and added to the note.");
      resetOcrState();
    } catch (err) {
      console.error("OCR failed:", err);
      const message =
        err?.message === "unreadable"
          ? "Could not read the selected image. Please try another image."
          : "Text extraction failed. Please try again.";
      setOcrError(message);
      toast.error(message);
    } finally {
      if (worker) {
        try {
          await worker.terminate();
        } catch (termErr) {
          console.error("Failed to terminate OCR worker:", termErr);
        }
      }
      setOcrRunning(false);
      setOcrProgress(0);
    }
  };

  // Release the last preview object URL if the page unmounts mid-flow.
  useEffect(
    () => () => {
      if (ocrPreviewUrlRef.current) URL.revokeObjectURL(ocrPreviewUrlRef.current);
    },
    [],
  );

  // ---------------- Toolbar button ----------------
  const ToolButton = ({ onClick, active, disabled, title, children }) => (
    <button
      type="button"
      className={`nt-tool${active ? " is-active" : ""}`}
      title={title}
      aria-label={title}
      aria-pressed={!!active}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      {children}
    </button>
  );

  const headingValue = (() => {
    if (!editor) return "paragraph";
    if (editor.isActive("heading", { level: 1 })) return "h1";
    if (editor.isActive("heading", { level: 2 })) return "h2";
    if (editor.isActive("heading", { level: 3 })) return "h3";
    return "paragraph";
  })();

  const applyHeading = (value) => {
    if (!editor) return;
    if (value === "paragraph") {
      editor.chain().focus().setParagraph().run();
      return;
    }
    const level = Number(value.replace("h", ""));
    editor.chain().focus().toggleHeading({ level }).run();
  };

  return (
    <div className="notes-page">
      <div className="notes-header">
        <div>
          <h1>Notes</h1>
          <p>Meeting notes, site records, and project documents.</p>
        </div>

        <button className="nt-primary-btn" onClick={startNewNote}>
          <FaPlus />
          New note
        </button>
      </div>

      <div className="notes-layout">
        {/* ---------------- Document list ---------------- */}
        <aside className="notes-sidebar">
          <div className="nt-search">
            <FaSearch />
            <input
              type="text"
              placeholder="Search notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="nt-list">
            {notesLoading ? (
              <div className="nt-list-empty">Loading notes...</div>
            ) : filteredNotes.length === 0 ? (
              <div className="nt-list-empty">
                {search.trim()
                  ? "No notes match that search."
                  : "No notes yet. Create your first document with New note."}
              </div>
            ) : (
              filteredNotes.map((note) => {
                const live = liveProjectFor(note);
                const isOrphan = note.noteType === "project" && !live;

                return (
                  <div
                    key={note.id}
                    className={`nt-list-item${activeId === note.id ? " is-active" : ""}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => openNote(note)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openNote(note);
                      }
                    }}
                  >
                    <div className="nt-list-item-main">
                      <span className="nt-list-title">
                        <FaRegFileAlt className="nt-list-icon" />
                        {note.title || "Untitled document"}
                      </span>

                      <span className="nt-list-meta">
                        <span
                          className={`nt-type-chip${
                            note.noteType === "project" ? " is-project" : ""
                          }`}
                        >
                          {note.noteType === "project" ? "Project" : "General"}
                        </span>

                        {note.noteType === "project" && (
                          <span className={`nt-project-ref${isOrphan ? " is-orphan" : ""}`}>
                            {isOrphan
                              ? "Project unavailable"
                              : `${live.name} · P-${formatProjectSeq(live.projectNumber)}`}
                          </span>
                        )}
                      </span>

                      <span className="nt-list-date">
                        Last edited {formatRelativeDate(note.updatedAt)}
                      </span>
                    </div>

                    <div className="nt-list-actions">
                      <button
                        type="button"
                        className="nt-list-pdf"
                        title="Generate PDF"
                        aria-label={`Generate PDF for ${note.title || "note"}`}
                        disabled={!!pdfGeneratingKey}
                        onClick={(e) => {
                          e.stopPropagation();
                          generatePdfForSavedNote(note);
                        }}
                      >
                        <FaFilePdf />
                      </button>

                      <button
                        type="button"
                        className="nt-list-delete"
                        title="Delete note"
                        aria-label={`Delete ${note.title || "note"}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingDelete(note);
                        }}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* ---------------- Editor ---------------- */}
        <section className="notes-workspace">
          {!isComposing ? (
            <div className="nt-placeholder">
              <FaRegFileAlt className="nt-placeholder-icon" />
              <h3>Open a note to start editing</h3>
              <p>Pick a document on the left, or create a new one.</p>
              <button className="nt-primary-btn" onClick={startNewNote}>
                <FaPlus />
                New note
              </button>
            </div>
          ) : (
            <>
              <div className="nt-doc-head">
                <input
                  type="text"
                  className="nt-title-input"
                  placeholder="Document title"
                  value={draft.title}
                  onChange={(e) => handleDraftChange("title", e.target.value)}
                />

                <div className="nt-doc-meta">
                  <div className="nt-type-toggle" role="radiogroup" aria-label="Note type">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={draft.noteType === "general"}
                      className={draft.noteType === "general" ? "is-active" : ""}
                      onClick={() => handleDraftChange("noteType", "general")}
                    >
                      General note
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={draft.noteType === "project"}
                      className={draft.noteType === "project" ? "is-active" : ""}
                      onClick={() => handleDraftChange("noteType", "project")}
                    >
                      Project note
                    </button>
                  </div>

                  {draft.noteType === "project" && (
                    <div className="nt-project-field">
                      <span className="nt-project-field-label">Project</span>
                      <NotesProjectPicker
                        projects={projectOptions}
                        value={draft.clientDocId}
                        loading={clientsLoading}
                        onChange={(clientDocId) => handleDraftChange("clientDocId", clientDocId)}
                      />
                    </div>
                  )}
                </div>

                {formError && <span className="nt-error">{formError}</span>}
              </div>

              {/* Formatting bar */}
              <div className="nt-toolbar" role="toolbar" aria-label="Formatting">
                <div className="nt-tool-group">
                  <ToolButton
                    title="Undo"
                    disabled={!editor?.can().undo()}
                    onClick={() => editor?.chain().focus().undo().run()}
                  >
                    <FaUndo />
                  </ToolButton>
                  <ToolButton
                    title="Redo"
                    disabled={!editor?.can().redo()}
                    onClick={() => editor?.chain().focus().redo().run()}
                  >
                    <FaRedo />
                  </ToolButton>
                </div>

                <div className="nt-tool-group">
                  <select
                    className="nt-block-select"
                    value={headingValue}
                    onChange={(e) => applyHeading(e.target.value)}
                    aria-label="Text style"
                  >
                    <option value="paragraph">Normal text</option>
                    <option value="h1">Heading 1</option>
                    <option value="h2">Heading 2</option>
                    <option value="h3">Heading 3</option>
                  </select>
                </div>

                <div className="nt-tool-group">
                  <ToolButton
                    title="Bold"
                    active={editor?.isActive("bold")}
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                  >
                    <FaBold />
                  </ToolButton>
                  <ToolButton
                    title="Italic"
                    active={editor?.isActive("italic")}
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                  >
                    <FaItalic />
                  </ToolButton>
                  <ToolButton
                    title="Underline"
                    active={editor?.isActive("underline")}
                    onClick={() => editor?.chain().focus().toggleUnderline().run()}
                  >
                    <FaUnderline />
                  </ToolButton>
                  <ToolButton
                    title="Strikethrough"
                    active={editor?.isActive("strike")}
                    onClick={() => editor?.chain().focus().toggleStrike().run()}
                  >
                    <FaStrikethrough />
                  </ToolButton>
                </div>

                <div className="nt-tool-group">
                  <ToolButton
                    title="Align left"
                    active={editor?.isActive({ textAlign: "left" })}
                    onClick={() => editor?.chain().focus().setTextAlign("left").run()}
                  >
                    <FaAlignLeft />
                  </ToolButton>
                  <ToolButton
                    title="Align center"
                    active={editor?.isActive({ textAlign: "center" })}
                    onClick={() => editor?.chain().focus().setTextAlign("center").run()}
                  >
                    <FaAlignCenter />
                  </ToolButton>
                  <ToolButton
                    title="Align right"
                    active={editor?.isActive({ textAlign: "right" })}
                    onClick={() => editor?.chain().focus().setTextAlign("right").run()}
                  >
                    <FaAlignRight />
                  </ToolButton>
                </div>

                <div className="nt-tool-group">
                  <ToolButton
                    title="Bullet list"
                    active={editor?.isActive("bulletList")}
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  >
                    <FaListUl />
                  </ToolButton>
                  <ToolButton
                    title="Numbered list"
                    active={editor?.isActive("orderedList")}
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  >
                    <FaListOl />
                  </ToolButton>
                </div>

                <div className="nt-tool-group">
                  <ToolButton title="Insert table" onClick={openTableModal}>
                    <FaTable />
                  </ToolButton>
                </div>

                {/* Image -> Text (OCR). Labelled rather than icon-only, since
                    the action isn't self-evident from an icon alone. */}
                <div className="nt-tool-group nt-tool-group--ocr">
                  <button
                    type="button"
                    className="nt-ocr-trigger"
                    title="Extract text from an image and insert it into this note"
                    disabled={ocrRunning}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openOcrModal}
                  >
                    <FaImage />
                    <span>{ocrRunning ? "Extracting text..." : "Image to Text"}</span>
                  </button>
                </div>
              </div>

              {/* Table controls — only while the caret is inside a table */}
              <AnimatePresence>
                {inTable && (
                  <motion.div
                    className="nt-table-bar"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.18, ease: "easeOut" }}
                  >
                    <span className="nt-table-bar-label">
                      <FaColumns /> Table
                    </span>
                    <button type="button" onClick={() => editor.chain().focus().addRowBefore().run()}>
                      Row above
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()}>
                      Row below
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().deleteRow().run()}>
                      Delete row
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().addColumnBefore().run()}>
                      Column left
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()}>
                      Column right
                    </button>
                    <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()}>
                      Delete column
                    </button>
                    <button
                      type="button"
                      className="is-danger"
                      onClick={() => editor.chain().focus().deleteTable().run()}
                    >
                      Delete table
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Document surface */}
              <div className="nt-editor-shell">
                <EditorContent editor={editor} className="nt-editor" />
              </div>

              <div className="nt-doc-footer">
                <span className="nt-save-state">
                  {saving
                    ? "Saving..."
                    : dirty
                    ? "Unsaved changes"
                    : lastSavedAt
                    ? `Saved ${formatRelativeDate(lastSavedAt).toLowerCase()}`
                    : "Not saved yet"}
                </span>

                <div className="nt-doc-actions">
                  {/* Only offered once the document actually exists in
                      Firestore — a brand-new unsaved draft has no activeId. */}
                  {activeId && (
                    <button
                      type="button"
                      className="nt-ghost-btn"
                      disabled={!!pdfGeneratingKey}
                      onClick={generatePdfForOpenNote}
                    >
                      <FaFilePdf />
                      {pdfGeneratingKey === activeId ? "Generating..." : "Generate PDF"}
                    </button>
                  )}
                  <button className="nt-ghost-btn" onClick={closeEditor}>
                    Close
                  </button>
                  <button className="nt-primary-btn" disabled={saving} onClick={() => saveNote()}>
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>

      {/* Insert table modal */}
      <AnimatePresence>
        {showTableModal && (
          <motion.div
            className="nt-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTableModal(false)}
          >
            <motion.div
              className="nt-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nt-modal-head">
                <h2>Insert table</h2>
                <button
                  type="button"
                  className="nt-modal-close"
                  aria-label="Close"
                  onClick={() => setShowTableModal(false)}
                >
                  <FaTimes />
                </button>
              </div>

              <div className="nt-modal-grid">
                <label>
                  Rows
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={tableRows}
                    onChange={(e) => setTableRows(e.target.value)}
                    autoFocus
                  />
                </label>
                <label>
                  Columns
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={tableCols}
                    onChange={(e) => setTableCols(e.target.value)}
                  />
                </label>
              </div>

              <p className="nt-modal-hint">
                The first row becomes a header. You can add or remove rows and columns
                afterwards from the table bar.
              </p>

              <div className="nt-modal-actions">
                <button className="nt-ghost-btn" onClick={() => setShowTableModal(false)}>
                  Cancel
                </button>
                <button className="nt-primary-btn" onClick={insertTable}>
                  Insert
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Image -> Text (OCR) modal. Reuses the same nt-modal-overlay /
          nt-modal shell as the Insert table modal, so it inherits the
          page's existing spacing, radius, shadow and button styles. */}
      <AnimatePresence>
        {showOcrModal && (
          <motion.div
            className="nt-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOcrModal}
          >
            <motion.div
              className="nt-modal nt-ocr-modal"
              initial={{ opacity: 0, scale: 0.94, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 12 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="nt-modal-head">
                <h2>Image to text</h2>
                <button
                  type="button"
                  className="nt-modal-close"
                  aria-label="Close"
                  disabled={ocrRunning}
                  onClick={closeOcrModal}
                >
                  <FaTimes />
                </button>
              </div>

              <input
                ref={ocrInputRef}
                type="file"
                className="nt-ocr-file-input"
                accept={OCR_ACCEPTED_TYPES.join(",")}
                onChange={handleOcrFileChange}
              />

              {!ocrFile ? (
                <div className="nt-ocr-drop">
                  <FaImage className="nt-ocr-drop-icon" />
                  <p>
                    Choose an image containing text — a quotation, a scanned page, a
                    screenshot or a photo of a note.
                  </p>
                  <button
                    type="button"
                    className="nt-ghost-btn"
                    onClick={() => ocrInputRef.current?.click()}
                  >
                    Choose image
                  </button>
                  <span className="nt-ocr-formats">
                    JPG, PNG, WEBP, GIF or BMP · up to 12 MB
                  </span>
                </div>
              ) : (
                <div className="nt-ocr-preview">
                  <img src={ocrPreviewUrl} alt={`Preview of ${ocrFile.name}`} />
                  <div className="nt-ocr-file-meta">
                    <span className="nt-ocr-file-name">{ocrFile.name}</span>
                    <span className="nt-ocr-file-sub">
                      {(ocrFile.type.split("/")[1] || "image").toUpperCase()} ·{" "}
                      {formatFileSize(ocrFile.size)}
                    </span>
                    <button
                      type="button"
                      className="nt-ocr-remove"
                      disabled={ocrRunning}
                      onClick={clearOcrFile}
                    >
                      Remove image
                    </button>
                  </div>
                </div>
              )}

              {ocrRunning && (
                <div className="nt-ocr-progress" role="status" aria-live="polite">
                  <span>Extracting text{ocrProgress ? ` — ${ocrProgress}%` : "..."}</span>
                  <div className="nt-ocr-progress-track">
                    <div
                      className="nt-ocr-progress-fill"
                      style={{ width: `${Math.max(ocrProgress, 6)}%` }}
                    />
                  </div>
                </div>
              )}

              {ocrError && (
                <span className="nt-error" role="alert">
                  {ocrError}
                </span>
              )}

              <p className="nt-modal-hint">
                The extracted text is inserted at your cursor as normal, editable text —
                nothing already in the note is replaced. The image itself is never
                uploaded or saved.
              </p>

              <div className="nt-modal-actions">
                <button
                  type="button"
                  className="nt-ghost-btn"
                  disabled={ocrRunning}
                  onClick={closeOcrModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="nt-primary-btn"
                  disabled={!ocrFile || ocrRunning}
                  onClick={runOcr}
                >
                  {ocrRunning ? "Extracting text..." : "Extract text"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Unsaved changes — replaces the old window.confirm() prompts. */}
      <ConfirmDialog
        open={!!pendingNavigation}
        title="Unsaved changes"
        message="You have unsaved changes in this note. Closing it now will discard them."
        variant="danger"
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={discardAndContinue}
        onCancel={() => setPendingNavigation(null)}
      />

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this note?"
        message={`"${pendingDelete?.title || "Untitled document"}" will be permanently removed. This only deletes the note — the client, project, and all billing records are untouched.`}
        variant="danger"
        loading={deleting}
        confirmLabel="Delete"
        onConfirm={confirmDeleteNote}
        onCancel={() => !deleting && setPendingDelete(null)}
      />

      <ToastContainer toasts={toasts} onDismiss={toast.dismiss} />
    </div>
  );
}

export default Notes;